import { Component } from 'preact';
import RuneSpinner from './RuneSpinner';
import {
  validateUsername,
  getUsernameRuleStatus,
  USERNAME_MAX_LENGTH,
} from '../utils/usernameValidation';
import {
  BG_ATMOSPHERE, VIGNETTE, TEXT_PRIMARY, TEXT_MUTED,
  DIALOG_STYLE, GOLD_BTN, BEVELED_BTN, INPUT_STYLE,
  FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';
import { k, t } from '../localization/t';

// getUsernameRuleStatus builds its labels from the length and charset
// constants, so the extractor cannot see the English text there. The
// rule set is fixed, so the keys are marked here and looked up by id;
// an unknown id falls back to the label the validator built.
const USERNAME_RULE_LABELS = {
  length: k('3–16 characters'),
  start: k('Starts with a letter'),
  chars: k('Only letters, numbers, and underscores'),
};

const RULE_OK_COLOR = '#7bbf6a';
const RULE_FAIL_COLOR = '#c45050';
const ERROR_COLOR = '#c45050';

export default class ArenaUsernamePrompt extends Component {
  constructor(props) {
    super(props);
    const initial = props.currentName || '';
    this.state = {
      username: initial,
      // Errors fall into two buckets: `localError` is the live
      // shape-check shown only when the user tries to submit invalid
      // input (so we don't shout at them while typing). `serverError`
      // is the response from the backend (uniqueness, network) and
      // clears on the next keystroke.
      localError: null,
      serverError: null,
      loading: false,
    };
  }

  handleInput = (e) => {
    this.setState({
      username: e.target.value,
      localError: null,
      serverError: null,
    });
  };

  handleSubmit = async () => {
    const result = validateUsername(this.state.username);
    if (!result.ok) {
      this.setState({ localError: result.message, serverError: null });
      return;
    }
    this.setState({ loading: true, localError: null, serverError: null });
    try {
      await this.props.onRegister(result.name);
    } catch (error) {
      this.setState({ loading: false, serverError: error.message });
    }
  };

  render() {
    const { username, localError, serverError, loading } = this.state;
    const rules = getUsernameRuleStatus(username);
    const localResult = validateUsername(username);
    const canSubmit = localResult.ok && !loading;
    const charCount = username.trim().length;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: BG_ATMOSPHERE }}>
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative w-full max-w-[28cqw] px-6">
          <div className="relative p-8" style={DIALOG_STYLE}>
            <FourCorners />

            <div className="text-center mb-6">
              <h1
                className="text-2xl font-bold arena-heading mb-2"
                style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 0 20px rgba(200,160,60,0.1)' }}
              >
                {t('Choose Your Username')}
              </h1>
              <p className="text-sm" style={{ color: TEXT_MUTED }}>
                {t('This will be visible to other players in matchmaking and on the leaderboard.')}
              </p>
            </div>

            <OrnamentalDivider className="mb-6" />

            <div className="flex flex-col gap-4">
              <div className="relative">
                <input
                  type="text"
                  value={username}
                  maxLength={USERNAME_MAX_LENGTH}
                  placeholder={t('Enter a unique username...')}
                  className="w-full px-4 py-3 text-lg text-center outline-none"
                  style={{
                    ...INPUT_STYLE,
                    borderRadius: 'var(--radius-lg)',
                    color: TEXT_PRIMARY,
                    fontSize: '1.125cqw',
                    letterSpacing: '0.02em',
                  }}
                  onInput={this.handleInput}
                  onKeyDown={(e) => { if (e.key === 'Enter') this.handleSubmit(); }}
                  autoFocus
                  spellcheck={false}
                  autocomplete="off"
                  autocapitalize="off"
                />
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{
                    color: charCount > USERNAME_MAX_LENGTH ? RULE_FAIL_COLOR : TEXT_MUTED,
                    fontSize: '0.75cqw',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {charCount}/{USERNAME_MAX_LENGTH}
                </span>
              </div>

              <ul className="flex flex-col gap-1.5" style={{ fontSize: '0.8125cqw' }}>
                {rules.map((rule) => {
                  let color = TEXT_MUTED;
                  let glyph = '·';
                  if (rule.active) {
                    color = rule.met ? RULE_OK_COLOR : RULE_FAIL_COLOR;
                    glyph = rule.met ? '✓' : '✗';
                  }
                  return (
                    <li key={rule.id} className="flex items-center gap-2" style={{ color }}>
                      <span
                        style={{
                          width: '1cqw',
                          display: 'inline-flex',
                          justifyContent: 'center',
                          fontWeight: 600,
                        }}
                      >
                        {glyph}
                      </span>
                      <span>{t(USERNAME_RULE_LABELS[rule.id] || rule.label)}</span>
                    </li>
                  );
                })}
              </ul>

              {localError || serverError ? (
                <div className="text-center text-sm" style={{ color: ERROR_COLOR }}>
                  {localError || serverError}
                </div>
              ) : null}

              <button
                type="button"
                disabled={!canSubmit}
                className="w-full py-3 text-sm font-semibold arena-heading cursor-pointer transition-all disabled:cursor-not-allowed"
                style={loading || canSubmit ? GOLD_BTN : { ...BEVELED_BTN, color: TEXT_MUTED, borderRadius: 'var(--radius-md)', opacity: 0.3 }}
                onMouseEnter={(e) => {
                  if (canSubmit) {
                    e.currentTarget.style.transform = 'scale(1.03)';
                    e.currentTarget.style.boxShadow = '0 0 30px rgba(212,168,67,0.3), inset 0 1px 0 rgba(255,255,255,0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (canSubmit) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = GOLD_BTN.boxShadow;
                  }
                }}
                onClick={this.handleSubmit}
              >
                {loading ? (
                  <span className="relative">
                    <span className="invisible">{t('Confirm Username')}</span>
                    <span className="absolute inset-0 flex items-center justify-center"><RuneSpinner size={20} /></span>
                  </span>
                ) : t('Confirm Username')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
